import { Injectable, NgZone, Signal, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { produce } from 'immer';
import { Observable, of } from 'rxjs';
import { catchError, defaultIfEmpty, filter, finalize, map, switchMap, tap } from 'rxjs/operators';
import { RuleSet, SeasonPlan } from '@coin/shared/core/util-models';
import { TinyHelpers } from '@coin/shared/core/util-helpers';
import { SeasonProcessState, SeasonTypeEnum } from '@coin/shared/core/util-enums';
import { FeatureFlagsService, LoadingService } from '@coin/shared/core/data-access';
import { GenericSeasonSettings, Season } from '@coin/shared/season-mgmt/util';
import { RuleEngineV2Service } from '@coin/shared/core/data-access';
import { SeasonPlanService, SeasonSettingsService } from '@coin/shared/season-mgmt/data-access';
import { SeasonSimulation } from '../models/season-simulation.model';
import { SeasonSimulationService } from '../services/season-simulation.service';
import { SeasonProcessStepNav } from '../interfaces/season-process-step-nav.interface';
import { GeneralSeasonService } from '../services/general-season.service';
import { SEASON_STEPS } from '../static/season-process-steps';
import { SpecialPaymentSeasonService } from '../services/special-payment-season.service';
import { ExceptionalRecognitionSeasonService } from '../services/exceptional-recognition-season.service';

export interface SeasonStateModel {
  currentSeason: Season;
  currentSimulation: SeasonSimulation;
  currentSeasonSettings: GenericSeasonSettings;
  currentPlan: SeasonPlan;
  breadcrumbItems: SeasonProcessStepNav[];
  ruleSet: RuleSet;
  simulations: SeasonSimulation[];
  plans: SeasonPlan[];
  ruleSetPopulation: {
    ruleSet: RuleSet;
    population: unknown;
    skip: number;
  };
}

// Auto-migrated from NGXS
@Injectable({ providedIn: 'root' })
export class SeasonState {
  private generalSeasonService = inject(GeneralSeasonService);
  private specialPaymentSeasonService = inject(SpecialPaymentSeasonService);
  private exceptionalRecognitionSeasonService = inject(ExceptionalRecognitionSeasonService);
  private seasonSettingsService = inject(SeasonSettingsService);
  private router = inject(Router);
  private ngZone = inject(NgZone);
  private seasonPlanService = inject(SeasonPlanService);
  private ruleEngineV2Service = inject(RuleEngineV2Service);
  private seasonSimulationService = inject(SeasonSimulationService);
  private loadingService = inject(LoadingService);
  private featureFlagsService = inject(FeatureFlagsService);

  private seasonStateModel = signal<SeasonStateModel>({
    currentSeason: null,
    currentSeasonSettings: null,
    currentSimulation: null,
    currentPlan: null,
    breadcrumbItems: [],
    ruleSet: null,
    ruleSetPopulation: {
      ruleSet: null,
      population: null,
      skip: 0
    },
    simulations: [],
    plans: []
  });

  public currentSeason = computed(() => this.seasonStateModel().currentSeason);

  public currentSeasonSettings = computed(() => this.seasonStateModel().currentSeasonSettings);

  public currentSimulation = computed(() => this.seasonStateModel().currentSimulation);

  public currentPlan = computed(() => this.seasonStateModel().currentPlan);

  public ruleSet = computed(() => this.seasonStateModel().ruleSet);

  public population = computed(() => this.seasonStateModel().ruleSetPopulation?.population);

  public simulations = computed(() => this.seasonStateModel().simulations);

  public plans = computed(() => this.seasonStateModel().plans);

  public currentProcessStep = computed(() => this.seasonStateModel().breadcrumbItems?.find(item => item.active));

  public nextProcessStep = computed(() => {
    const state = this.seasonStateModel();
    for (let i = 0; i < state.breadcrumbItems?.length; i++) {
      if (state.breadcrumbItems[i].active) {
        return state.breadcrumbItems[i + 1];
      }
    }
    return undefined;
  });

  public prevProcessStep = computed(() => {
    const state = this.seasonStateModel();
    for (let i = 0; i < state.breadcrumbItems?.length; i++) {
      if (state.breadcrumbItems[i].active) {
        return state.breadcrumbItems[i - 1];
      }
    }
    return undefined;
  });

  public getProcessStepById(id: string): Signal<SeasonProcessStepNav> {
    return computed(() => this.seasonStateModel().breadcrumbItems?.find(item => item.id === id));
  }

  public getProcessStepByRoute(route: string): Signal<SeasonProcessStepNav> {
    return computed(() => this.seasonStateModel().breadcrumbItems?.find(item => item.route === route));
  }

  setCurrentProcessStep(payload: string): void {
    this.seasonStateModel.update(currentState =>
      produce(currentState, state => {
        for (const item of state.breadcrumbItems) {
          item.active = item.id === payload;
        }
      })
    );
  }

  setRuleSet(payload?: RuleSet): void {
    this.seasonStateModel.update(currentState =>
      produce(currentState, state => {
        if (payload && !TinyHelpers.areItemsSimilar(payload, state.ruleSet)) {
          state.ruleSet = structuredClone(payload);
        }
      })
    );
  }

  loadRuleSet(payload: { ruleSetId: string }): Observable<void> {
    return this.ruleEngineV2Service.getRuleSet(payload.ruleSetId, false).pipe(
      switchMap(res => {
        this.setRuleSet(res as unknown as RuleSet);
        return of(undefined);
      })
    );
  }

  loadSimulations(payload: { seasonId: string; seasonType: SeasonTypeEnum }): Observable<void> {
    return this.seasonSimulationService.getSimulations(payload.seasonType, payload.seasonId).pipe(
      map((res: SeasonSimulation[]) => {
        this.seasonStateModel.update(currentState =>
          produce(currentState, state => {
            if (!TinyHelpers.areItemsSimilar(res, state.simulations)) {
              state.simulations = res;
            }
          })
        );
      })
    );
  }

  updateSimulation(payload: {
    simulation: SeasonSimulation;
    seasonType: SeasonTypeEnum;
  }): Observable<void | SeasonSimulation> {
    return this.seasonSimulationService.updateSimulation(payload.seasonType, payload.simulation).pipe(
      tap(() => {
        this.seasonStateModel.update(currentState =>
          produce(currentState, state => {
            state.simulations = state.simulations?.map(sim =>
              sim.id === payload?.simulation?.id ? { ...sim, ...payload } : sim
            );
          })
        );
      })
    );
  }

  setPlans(payload: SeasonPlan[]): void {
    this.seasonStateModel.update(currentState =>
      produce(currentState, state => {
        if (!TinyHelpers.areItemsSimilar(payload, state.plans)) {
          state.plans = payload;
        }
      })
    );
  }

  updateSeasonPlan(payload: SeasonPlan): void {
    this.seasonStateModel.update(currentState =>
      produce(currentState, state => {
        const foundIndex = state.plans.findIndex(plan => plan.id === payload.id);
        state.plans[foundIndex] = { ...state.plans[foundIndex], ...payload };
        if (state.currentPlan?.id === state.plans[foundIndex]?.id) {
          state.currentPlan = state.plans[foundIndex];
        }
      })
    );
  }

  updatePlanTitle(payload: {
    plan: SeasonPlan;
    newTitle: string;
    displayName: string;
    seasonType: SeasonTypeEnum;
  }): Observable<void> {
    return this.seasonPlanService
      .updatePlan(payload.seasonType, { ...payload.plan, title: payload.newTitle, displayName: payload.displayName })
      .pipe(
        map((res: SeasonPlan) => {
          this.seasonStateModel.update(currentState =>
            produce(currentState, state => {
              const changedPlan = state.plans.find(plan => plan.id === payload.plan.id);
              changedPlan.title = payload.newTitle;
              changedPlan.displayName = payload.displayName;
              changedPlan.version = res.version;
            })
          );
        })
      );
  }

  deletePlan(payload: { plan: SeasonPlan; seasonType: SeasonTypeEnum }): Observable<void> {
    return this.seasonPlanService.deletePlan(payload.seasonType, payload.plan.id).pipe(
      switchMap(() =>
        this.setCurrentSeason(
          this.seasonStateModel().currentSeason?.id,
          this.seasonStateModel().currentSeason?.seasonType
        )
      ),
      map(() => undefined)
    );
  }

  setCurrentSeason(seasonId: string, seasonType: SeasonTypeEnum): Observable<unknown> {
    return this.generalSeasonService.getSeasonById(seasonId, seasonType).pipe(
      tap((season: Season) => {
        this.seasonStateModel.update(currentState =>
          produce(currentState, state => {
            if (season) {
              state.plans = season?.plans || [];
              state.currentSeason = season;

              if (!state.breadcrumbItems?.length) {
                state.breadcrumbItems = structuredClone(SEASON_STEPS[season.seasonType]).filter(
                  step => !step.featureFlag || this.featureFlagsService.isActive(step.featureFlag)
                );
              }
            }
          })
        );
      }),
      // ngxs compatibility: the dispatch emitted once even when the settings request emitted nothing
      switchMap(() => this.setCurrentSeasonSettings().pipe(defaultIfEmpty(undefined))),
      catchError(err => {
        this.ngZone.run(() => this.router.navigate(['/season-mgmt/season-overview']));
        return err;
      })
    );
  }

  updateCurrentSeason(payload: Season): Observable<Season> {
    return this.generalSeasonService.updateSeason(payload).pipe(
      tap(season => {
        this.seasonStateModel.update(currentState =>
          produce(currentState, state => {
            state.currentSeason = season;
          })
        );
      })
    );
  }

  setCurrentSeasonSettings(): Observable<GenericSeasonSettings> {
    const season = this.seasonStateModel().currentSeason;

    if (
      ![
        SeasonTypeEnum.Merit,
        SeasonTypeEnum.SpecialPaymentCash,
        SeasonTypeEnum.SpecialPaymentEquity,
        SeasonTypeEnum.PositionEvaluation,
        SeasonTypeEnum.IncentiveV2,
        SeasonTypeEnum.ExceptionalRecognitions
      ].includes(season?.seasonType)
    ) {
      return of(null);
    }

    return this.seasonSettingsService.getSeasonSettings(season?.id, season?.seasonType).pipe(
      filter(settings => !!settings),
      catchError(() => of(null)),
      tap(currentSeasonSettings => this.seasonStateModel.update(state => ({ ...state, currentSeasonSettings })))
    );
  }

  updateCurrentSeasonSettings(
    settings: GenericSeasonSettings,
    seasonType: SeasonTypeEnum
  ): Observable<GenericSeasonSettings> {
    return this.seasonSettingsService.updateSeasonSettings(settings, seasonType).pipe(
      filter(settings => !!settings),
      tap(currentSeasonSettings => this.seasonStateModel.update(state => ({ ...state, currentSeasonSettings })))
    );
  }

  updateSpecialPaymentSeasonState(
    seasonId: string,
    newState: SeasonProcessState.Monitoring | SeasonProcessState.Evaluation
  ): Observable<Season> {
    return this.specialPaymentSeasonService.updateSeasonState(seasonId, newState).pipe(
      tap(season => {
        this.seasonStateModel.update(currentState =>
          produce(currentState, state => {
            state.currentSeason = season;
          })
        );
      })
    );
  }

  updateExceptionalRecognitionSeasonState(seasonId: string, newState: SeasonProcessState): Observable<Season> {
    return this.exceptionalRecognitionSeasonService.updateSeasonState(seasonId, newState).pipe(
      tap(season => {
        this.seasonStateModel.update(currentState =>
          produce(currentState, state => {
            state.currentSeason = season;
          })
        );
      })
    );
  }

  setCurrentPlan(payload: { seasonId: string; seasonType: SeasonTypeEnum; planId: string }): Observable<unknown> {
    return this.seasonPlanService.getPlanById(payload.seasonType, payload.planId).pipe(
      tap((plan: SeasonPlan) => {
        this.seasonStateModel.update(currentState =>
          produce(currentState, state => {
            if (plan) {
              state.currentPlan = plan;

              if (!state.plans.some(storedPlan => storedPlan.id === plan.id)) {
                state.plans.push(plan);
              }
            }
          })
        );
      }),
      catchError(err => {
        this.ngZone.run(() => this.router.navigate(['/season-mgmt/season-overview']));
        return err;
      })
    );
  }

  setCurrentSimulation(payload: { simulationId: string; seasonType: SeasonTypeEnum }): Observable<unknown> {
    this.loadingService.present();
    return this.seasonSimulationService.getSimulationById(payload.seasonType, payload.simulationId).pipe(
      tap((sim: SeasonSimulation) => {
        this.seasonStateModel.update(currentState =>
          produce(currentState, state => {
            if (sim) {
              state.currentSimulation = sim;

              if (!state.simulations.some(storedSim => storedSim.id === sim.id)) {
                state.simulations.push(sim);
              }
            }
          })
        );
      }),
      catchError(err => {
        this.ngZone.run(() => this.router.navigate(['/season-mgmt/season-overview']));
        return err;
      }),
      finalize(() => this.loadingService.dismiss())
    );
  }

  setSeasonState(payload: SeasonProcessState): void {
    this.seasonStateModel.update(currentState =>
      produce(currentState, state => {
        if (payload) {
          state.currentSeason.state = payload;
        }
      })
    );
  }

  setPlan(payload: SeasonPlan): void {
    this.seasonStateModel.update(currentState =>
      produce(currentState, state => {
        if (payload) {
          state.currentPlan = payload;
        }
      })
    );
  }

  resetCurrentSeason(): void {
    this.seasonStateModel.update(currentState =>
      produce(currentState, state => {
        state.currentSeason = null;
        state.currentSeasonSettings = null;
        state.currentPlan = null;
        state.currentSimulation = null;
        state.breadcrumbItems = [];
        state.ruleSet = null;
        state.ruleSetPopulation = {
          population: null,
          ruleSet: null,
          skip: 0
        };
        state.simulations = [];
        state.plans = [];
      })
    );
  }

  resetCurrentPlan(): void {
    this.seasonStateModel.update(currentState =>
      produce(currentState, state => {
        state.currentPlan = null;
      })
    );
  }
}
